import React from 'react';
import { Building2, Map, TrendingUp, Users, FileText, MapPin } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import Newsletter from '../components/Newsletter';

const RealEstateSolutions = () => {
  const solutions = [
    {
      icon: Map,
      title: "Land Acquisition Strategy",
      description: "Expert site selection, due diligence, and negotiation support for government and private development projects"
    },
    {
      icon: FileText,
      title: "Feasibility Studies",
      description: "Comprehensive financial modeling, market analysis, and risk assessment for large-scale developments"
    },
    {
      icon: Building2,
      title: "Multi-Unit Planning",
      description: "Commercial and residential project design aligned with regional demographics and market demand"
    },
    {
      icon: TrendingUp,
      title: "North American Planning Standards",
      description: "World-class development frameworks adapted to Middle Eastern market conditions"
    },
    {
      icon: Users,
      title: "Public-Private Partnerships",
      description: "Structuring collaborative frameworks between government entities and private developers"
    },
    {
      icon: MapPin,
      title: "Smart City Infrastructure",
      description: "Future-ready urban planning with integrated technology and sustainability frameworks"
    }
  ];

  const capabilities = [
    "Site selection and land banking strategy",
    "Financial feasibility and pro forma analysis",
    "Zoning and regulatory compliance",
    "Infrastructure integration planning",
    "Community impact assessments",
    "Phased development roadmaps",
    "Procurement and contractor management",
    "Post-completion asset optimization"
  ];

  return (
    <div className="min-h-screen bg-black">
      {/* Hero Section with Urban Development Blueprint */}
      <section className="relative min-h-[85vh] flex items-center justify-center overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img 
            src="https://customer-assets.emergentagent.com/job_consulting-synergy/artifacts/wgieivtk_3BB1B7D2-74F5-4A2A-84B0-AAFBDB3A6714.png"
            alt="Urban Development Blueprint"
            className="w-full h-full object-cover"
          />
        </div>
        {/* Dark Overlay */}
        <div className="absolute inset-0 bg-black/65"></div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 text-center py-32">
          <h1 className="luxury-heading text-5xl md:text-7xl text-white mb-8 leading-tight">
            Land Acquisition &
            <span className="text-[#C9A55A]"> Feasibility Advisory</span>
          </h1>
          <p className="text-xl md:text-2xl text-gray-200 mb-12 max-w-4xl mx-auto leading-relaxed">
            Data-driven planning, cost modeling, and development strategy aligned with the Middle East's long-term housing vision.
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <Button 
              asChild
              className="luxury-button bg-[#C9A55A] text-black hover:bg-[#B8941F] font-semibold text-lg px-12 py-7 smooth-transition shadow-xl"
            >
              <Link to="/contact">
                Request Consultation
              </Link>
            </Button>
            <Button 
              asChild
              variant="outline"
              className="border-2 border-[#C9A55A] text-[#C9A55A] hover:bg-[#C9A55A] hover:text-black font-semibold text-lg px-12 py-7 smooth-transition"
            >
              <Link to="/services">
                View All Services
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Spacer */}
      <div className="h-20 bg-black"></div>

      {/* Solutions Grid */}
      <section className="py-32 bg-gradient-to-b from-black to-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="text-center mb-20">
            <h2 className="luxury-heading text-5xl md:text-6xl text-white mb-6">Comprehensive Development Solutions</h2>
            <p className="text-xl text-gray-500 max-w-3xl mx-auto leading-relaxed">
              From initial site selection to project completion and beyond
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {solutions.map((solution, index) => (
              <Card key={index} className="luxury-card bg-gradient-to-br from-white/5 to-white/[0.02] border-white/10 hover:border-[#C9A55A]/30 group cursor-pointer">
                <CardContent className="p-10">
                  <div className="w-16 h-16 bg-[#C9A55A]/10 rounded-lg flex items-center justify-center mb-8 group-hover:bg-[#C9A55A]/20 smooth-transition border border-[#C9A55A]/20">
                    <solution.icon className="text-[#C9A55A]" size={32} />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-4">{solution.title}</h3>
                  <p className="text-gray-400 leading-relaxed">{solution.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* North American Expertise */}
      <section className="py-32 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="luxury-heading text-4xl md:text-5xl text-white mb-8">
                North American Development Expertise
              </h2>
              <div className="space-y-6 text-gray-300 leading-relaxed">
                <p className="text-lg">
                  Syndicate Consultants brings <span className="text-[#C9A55A] font-semibold">decades of North American real estate development experience</span> to the Middle East, providing strategic advisory that bridges Western planning standards with regional market realities.
                </p>
                <p className="text-lg">
                  Our team understands the unique challenges of large-scale housing projects, commercial developments, and mixed-use urban centers in rapidly growing markets.
                </p>
                <p className="text-lg italic text-gray-400">
                  We deliver feasibility studies, financial modeling, and development roadmaps that government ministries and private developers can trust.
                </p>
              </div>
            </div>
            <div>
              <Card className="bg-gradient-to-br from-white/5 to-white/[0.02] border-white/10">
                <CardContent className="p-10">
                  <h3 className="text-2xl font-semibold text-white mb-6">Core Capabilities</h3>
                  <div className="space-y-4">
                    {capabilities.map((capability, index) => (
                      <div key={index} className="flex items-start space-x-3">
                        <div className="w-6 h-6 bg-[#C9A55A]/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                          <span className="text-[#C9A55A] text-xs font-bold">✓</span>
                        </div>
                        <span className="text-gray-300 leading-relaxed">{capability}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Project Types */}
      <section className="py-32 bg-gradient-to-b from-[#0a0a0a] to-black">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="text-center mb-20">
            <h2 className="luxury-heading text-5xl md:text-6xl text-white mb-6">Project Types</h2>
            <p className="text-xl text-gray-500 max-w-3xl mx-auto leading-relaxed">
              Specialized advisory for diverse development initiatives
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <Card className="bg-white/5 border-white/10 hover:border-[#C9A55A]/30 smooth-transition">
              <CardContent className="p-10">
                <h3 className="text-2xl font-bold text-white mb-4">Large-Scale Residential Housing</h3>
                <p className="text-gray-400 mb-6">Multi-unit housing projects addressing national housing needs with efficient design and construction</p>
                <ul className="space-y-2 text-gray-300 text-sm">
                  <li>• Master-planned communities</li>
                  <li>• Affordable housing developments</li>
                  <li>• Mixed-income residential projects</li>
                  <li>• Phased delivery strategies</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="bg-white/5 border-white/10 hover:border-[#C9A55A]/30 smooth-transition">
              <CardContent className="p-10">
                <h3 className="text-2xl font-bold text-white mb-4">Commercial & Mixed-Use Development</h3>
                <p className="text-gray-400 mb-6">Strategic planning for retail, office, and integrated urban centers</p>
                <ul className="space-y-2 text-gray-300 text-sm">
                  <li>• Shopping centers and malls</li>
                  <li>• Office tower complexes</li>
                  <li>• Mixed-use live-work-play districts</li>
                  <li>• Transit-oriented developments</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="bg-white/5 border-white/10 hover:border-[#C9A55A]/30 smooth-transition">
              <CardContent className="p-10">
                <h3 className="text-2xl font-bold text-white mb-4">Infrastructure & Public Works</h3>
                <p className="text-gray-400 mb-6">Essential infrastructure planning for new developments and urban expansion</p>
                <ul className="space-y-2 text-gray-300 text-sm">
                  <li>• Road and transportation networks</li>
                  <li>• Utility and services planning</li>
                  <li>• Parks and public spaces</li>
                  <li>• Community facilities</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="bg-white/5 border-white/10 hover:border-[#C9A55A]/30 smooth-transition">
              <CardContent className="p-10">
                <h3 className="text-2xl font-bold text-white mb-4">Smart City Planning</h3>
                <p className="text-gray-400 mb-6">Future-ready urban development with integrated technology and sustainability</p>
                <ul className="space-y-2 text-gray-300 text-sm">
                  <li>• IoT and smart infrastructure</li>
                  <li>• Energy-efficient design standards</li>
                  <li>• Digital connectivity planning</li>
                  <li>• Sustainable development frameworks</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Urban Development Planning & Project Structuring */}
      <section className="py-32 bg-black">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="luxury-heading text-4xl md:text-5xl text-white mb-8">
                Urban Development Planning & Project Structuring
              </h2>
              <div className="space-y-6 text-gray-300 leading-relaxed">
                <p className="text-lg">
                  Syndicate Consultants provides <span className="text-[#C9A55A] font-semibold">comprehensive master-planning services</span> for large-scale urban developments, combining North American expertise with regional market intelligence.
                </p>
                <p className="text-lg">
                  Our planning approach includes:
                </p>
                <ul className="space-y-3 text-gray-300">
                  <li className="flex items-start space-x-3">
                    <span className="text-[#C9A55A] mt-1">•</span>
                    <span><strong>Master Planning:</strong> Long-term development vision, phasing strategies, and infrastructure integration</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <span className="text-[#C9A55A] mt-1">•</span>
                    <span><strong>Zoning & Land Use Optimization:</strong> Density analysis, mixed-use structuring, and regulatory compliance</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <span className="text-[#C9A55A] mt-1">•</span>
                    <span><strong>Multi-Unit Project Modeling:</strong> Financial feasibility, unit mix optimization, and market absorption studies</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <span className="text-[#C9A55A] mt-1">•</span>
                    <span><strong>Urban Growth Modeling:</strong> Population forecasting, infrastructure capacity planning, and service delivery frameworks</span>
                  </li>
                </ul>
                <p className="text-gray-400 italic pt-4">
                  We transform complex development challenges into actionable, financially viable roadmaps that align with national housing priorities.
                </p>
              </div>
            </div>
            <div className="relative rounded-lg overflow-hidden shadow-2xl">
              <img 
                src="https://customer-assets.emergentagent.com/job_consulting-synergy/artifacts/r5hcbf3x_5A954618-1450-4076-B878-38925495D20A.png" 
                alt="Urban Development Planning"
                className="w-full h-auto"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Why North American Standards */}
      <section className="py-32 bg-gradient-to-b from-black to-[#0a0a0a]">
        <div className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12">
          <h2 className="luxury-heading text-4xl md:text-5xl text-white mb-12 text-center">Why North American Planning Standards?</h2>
          <div className="space-y-6 text-gray-300 text-lg leading-relaxed">
            <p>
              North American real estate markets are among the most sophisticated in the world, with rigorous feasibility requirements, proven financial models, and decades of best practices in large-scale development.
            </p>
            <p>
              By applying these standards to Middle Eastern projects, we help government ministries and private developers:
            </p>
            <ul className="space-y-3 ml-6">
              <li>• Reduce risk through proven methodologies</li>
              <li>• Attract international financing with credible projections</li>
              <li>• Accelerate approvals with comprehensive planning</li>
              <li>• Deliver projects on time and within budget</li>
              <li>• Create long-term value for communities</li>
            </ul>
            <p className="text-[#C9A55A] italic pt-6">
              Our expertise bridges continents, delivering world-class development solutions adapted to regional needs.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-b from-black to-[#0a0a0a]">
        <div className="max-w-4xl mx-auto px-6 sm:px-8 lg:px-12 text-center">
          <h2 className="luxury-heading text-4xl md:text-5xl text-white mb-6">
            Ready to Plan Your Next Development?
          </h2>
          <p className="text-xl text-gray-400 mb-8 leading-relaxed">
            Contact us for a confidential feasibility consultation and strategic development assessment
          </p>
          <Button 
            asChild
            className="luxury-button bg-[#C9A55A] text-black hover:bg-[#B8941F] font-semibold text-lg px-12 py-7"
          >
            <Link to="/contact">Request Consultation</Link>
          </Button>
        </div>
      </section>

      <Newsletter />
    </div>
  );
};

export default RealEstateSolutions;